"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { ShieldCheckIcon } from "@heroicons/react/24/outline";
import { useRouter } from "next/navigation";
import { Controller, useForm } from "react-hook-form";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { signIn, getSession } from "next-auth/react";
import type { Session } from "next-auth";
import { Button } from "@/components/ui/button/Button";
import { Card } from "@/components/ui/card/Card";
import { FormField } from "@/components/ui/form-field/FormField";
import { Input } from "@/components/ui/input/Input";
import { EyeCloseIcon, EyeIcon } from "@/icons";
import { SignInSchema, type SignInInput } from "@/lib/definitions";
import { useSendOtpMutation } from "@/hooks/api/useUserProfile";
import { useEmailVerificationStore } from "@/store/emailVerificationStore";
import { toast } from "@/lib/toast";

function hasAuthProfile(user: Session["user"]): boolean {
  return Boolean(user.role) && typeof user.onboarded !== "undefined";
}

export default function SignInForm() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const sendOtpMutation = useSendOtpMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInInput>({
    resolver: zodResolver(SignInSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  async function onSubmit(values: SignInInput) {
    setSignInError(null);
    setIsPending(true);

    try {
      const res = await signIn("credentials", {
        redirect: false,
        username: values.username,
        password: values.password,
      });

      if (res?.error) {
        setSignInError("Invalid email or password. Please try again.");
        setIsPending(false);
        return;
      }

      let session: Session | null = null;
      let retries = 0;
      const maxRetries = 10;

      while (retries < maxRetries) {
        session = await getSession();

        if (session?.user && hasAuthProfile(session.user)) {
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, 100));
        retries++;
      }

      let targetPath = "/";

      if (session?.user) {
        const user = session.user;

        // Check if email has been verified
        if (!user.emailVerified) {
          // Save email for OTP verification flow
          const { setVerificationData } = useEmailVerificationStore.getState();
          setVerificationData({
            email: user.email || "",
          });

          // Trigger email OTP request
          await sendOtpMutation.mutateAsync({
            channel: "EMAIL",
            email: user.email || "",
          });

          // Start the resend countdown
          useEmailVerificationStore.getState().setLastOtpSentAt(Date.now());

          toast.success("Please verify your business email to continue.");
          router.push("/verify-email");
          return;
        }

        // Check if user has an active subscription (Commented out: user automatically on trial plan)
        // const hasSubscription = user.hasSubscription;
        // if (!hasSubscription) {
        //   router.push("/pricing");
        //   return;
        // }

        const isAdmin = user.role === "ADMIN" || user.role === "SUPER_USER";
        const onboarded = user.onboarded === true;

        if (isAdmin || onboarded) {
          targetPath = "/dashboard";
        } else if (user.role === "USER") {
          targetPath = "/onboarding";
        }
      }

      router.push(targetPath);
      router.refresh();
    } catch {
      setSignInError("An unexpected error occurred. Please try again.");
      setIsPending(false);
    }
  }

  const passwordToggle = (
    <button
      type="button"
      onClick={() => setShowPassword((value) => !value)}
      className="cursor-pointer select-none"
      tabIndex={-1}
      aria-label={showPassword ? "Hide password" : "Show password"}
    >
      {showPassword ? (
        <EyeIcon className="fill-gray-500 dark:fill-gray-400" />
      ) : (
        <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400" />
      )}
    </button>
  );

  return (
    <div className="flex min-h-screen flex-col bg-section">
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-10">
        <Card className="w-full max-w-120">
          <Link href="/" className="mb-8 flex justify-center">
            <Image
              src="/images/logo/logo.webp"
              width={120}
              height={40}
              alt="Repayas"
              priority
              className="h-auto"
            />
          </Link>

          <div className="mb-8 text-center">
            <h1 className="mb-2 text-2xl font-medium text-text-primary dark:text-white/90 sm:text-3xl">
              Welcome back
            </h1>
            <p className="text-sm text-text-secondary dark:text-gray-400">
              Your Safe-to-Spend is ready.
            </p>
          </div>

          {signInError ? (
            <div className="mb-5 rounded-lg border border-error-200 bg-error-50 px-4 py-3 dark:border-error-500/20 dark:bg-error-500/10">
              <p className="text-sm font-medium text-error-600 dark:text-error-400">
                {signInError}
              </p>
            </div>
          ) : null}

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-5"
            noValidate
          >
            <FormField
              label="Username"
              htmlFor="signinUsername"
              error={errors.username?.message}
            >
              <Controller
                name="username"
                control={control}
                render={({ field }) => (
                  <Input
                    id="signinUsername"
                    type="text"
                    {...field}
                    value={field.value ?? ""}
                    placeholder="Enter your username"
                    autoComplete="username"
                    disabled={isPending}
                    error={!!errors.username}
                  />
                )}
              />
            </FormField>

            <FormField
              label="Password"
              htmlFor="signinPassword"
              error={errors.password?.message}
            >
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <Input
                    id="signinPassword"
                    type={showPassword ? "text" : "password"}
                    {...field}
                    value={field.value ?? ""}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    disabled={isPending}
                    error={!!errors.password}
                    rightElement={passwordToggle}
                  />
                )}
              />
              <Link
                href="/reset-password"
                className="mt-3 inline-block text-sm font-medium hover:underline transition-colors"
              >
                Forgot password?
              </Link>
            </FormField>

            <div className="pt-1">
              <Button
                type="submit"
                disabled={isPending}
                loading={isPending}
                fullWidth
                variant="primary"
                size="md"
                rounded="lg"
                className="h-12! text-base! text-white"
              >
                Sign in
              </Button>
            </div>
          </form>

          <p className="mt-6 text-center text-sm text-text-secondary ">
            New to Repayas?{" "}
            <Link
              href="/signup"
              className=" text-text-primary hover:underline "
            >
              Start your free 14-day trial
            </Link>
          </p>
        </Card>

        <p className="mt-6 flex max-w-md items-center justify-center gap-2 px-4 text-center text-xs text-text-secondary dark:text-gray-400">
          <ShieldCheckIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            Your data is never shared or sold. Open Banking secured by
            TrueLayer.
          </span>
        </p>
      </div>

      <footer className="flex justify-center border-t border-gray-200 py-8 gap-6 px-4 pb-8 text-xs text-gray-400 dark:text-text-secondary">
        <Link
          href="/terms"
          className="hover:text-text-secondary dark:hover:text-gray-300"
        >
          Terms
        </Link>
        <Link
          href="/privacy"
          className="hover:text-text-secondary dark:hover:text-gray-300"
        >
          Privacy
        </Link>
        <Link
          href="/cookies"
          className="hover:text-text-secondary dark:hover:text-gray-300"
        >
          Cookies
        </Link>
      </footer>
    </div>
  );
}
